import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import axiosInstance from '../components/axios';
import useLocationGraphConfig from '../components/hooks/useLocationGraphConfig';
import useHotkeyHandlers from '../components/hooks/useHotkeyHandlers';
import DataViewSwitch from '../components/data_view_switch';
import SystemDischargeTrendReport from '../components/system_discharge_trend_report';
import SystemDischargeTrendGraph from '../components/system_discharge_trend_graph';
import { parseDateTime, formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';


// System Trend sub-page rendering (Location Name, Title)
export default function SystemDischargeTrend() {
	const { locationNum, eventDateTime } = useParams();
	const [ searchParams ] = useSearchParams();

	const [trendData, setTrendData] = useState({});
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits/discharge`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales`
	});
	
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(1);
	
	// Check if current measurement is disabled from limits data
	const noCurrentMeasurement = limitData?.no_current_measurement ?? false;
	
	const graphTypeRef = useRef();
	const eventDateObj = parseDateTime(eventDateTime);

	// Helpers to check if trendData is empty and if DataViewSwitch should be disabled
	const isTrendDataEmpty = !trendData || (Object.keys(trendData).length === 0);
	const isDataSwitchDisabled = isLoading || isTrendDataEmpty;
	const dataSwitchDisabledText = isTrendDataEmpty ? "No data available to graph" : "Data is loading...";

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for graph navigation
	useHotkeyHandlers([
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage/Current/Power -> Voltage
						setGraphType(1);
						break;
					case 1:  // Voltage -> Current
						setGraphType(noCurrentMeasurement ? 3 : 2);
						break;
					case 2:  // Current -> Temperature
						setGraphType(3);
						break;
					case 3:  // Temperature -> Voltage/Current/Power
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);
	
	// Get the list of trend data for the specified location id
	useEffect(() => {
		setIsLoading(true);

		// Get System Trend Data
		axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/system`)
			.then(response => {
				setTrendData(response.data);
			})
			.catch(error => {
				console.error("Error fetching discharge trend data:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [locationNum, eventDateTime]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
	
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							System Discharge Trend: {formatDateTime(eventDateObj)}
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Power</option>
									<option key={1} value={1}>Voltage</option>
									{!noCurrentMeasurement && <option key={2} value={2}>Current</option>}
									<option key={3} value={3}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<DataViewSwitch
							checked={graphDisplay}
							onChange={handleViewChange}
							leftText="Report"
							rightText="Graph"
							disabled={isDataSwitchDisabled}
							disabledText={dataSwitchDisabledText}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<SystemDischargeTrendGraph
					trendData={trendData}
					limitData={limitData}
					graphScalesData={graphScalesData}
					graphType={graphType}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			:
				<SystemDischargeTrendReport
					trendData={trendData}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			}
		</Paper>
	);
}